<script setup>
import { ref, onMounted, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});
const hasError = ref(false);
const showGallery = ref(false);
const blobUrl = ref(null);
const isLoading = ref(true);

const { filteredCurrentChatAttachments } = useMessageContext();

// Fetch image with headers to bypass ngrok warning page in development
const loadImage = async () => {
  const url = props.attachment?.dataUrl;
  if (!url) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    const response = await fetch(url, {
      headers: { 'ngrok-skip-browser-warning': 'true' },
    });

    if (!response.ok) {
      throw new Error('Failed to load image');
    }

    const blob = await response.blob();
    blobUrl.value = URL.createObjectURL(blob);
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadImage);
watch(() => props.attachment?.dataUrl, loadImage);

const handleError = () => {
  hasError.value = true;
};
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <div
      v-else-if="isLoading"
      class="flex items-center justify-center size-full bg-n-alpha-1"
    >
      <Icon icon="i-lucide-loader-2" class="text-n-slate-11 animate-spin" />
    </div>
    <img
      v-else
      class="object-cover w-full h-full skip-context-menu"
      :src="blobUrl"
      @error="handleError"
    />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
